import { queryClient } from '@/lib/api-client'
import { nativeQueueManager } from '@/lib/nativeSync/nativeQueueManager'
import { isNativePlatform } from '@/lib/platform/platformDetection'
import { MIN_REFRESH_DURATION } from './pullToRefreshConstants'

export async function triggerRefresh(): Promise<void> {
  const minDuration = new Promise<void>(resolve =>
    setTimeout(resolve, MIN_REFRESH_DURATION)
  )

  // On iOS/Android native, the prefetch system caches 500+ individual issue
  // queries (singleIssue, singleIssueComments, singleIssueEvents).
  // invalidateQueries with a prefix key matches ALL of them, triggering 1500+
  // parallel refetches + PersistQueryClientProvider dehydration storms → OOM.
  //
  // Fix: on native, mark per-issue caches as stale (refetchType: 'none') so
  // they refetch lazily when the user opens them, but only actively refetch
  // the list/summary queries which are few and small.
  const isNative = isNativePlatform()

  const invalidate = Promise.all([
    // List and summary queries — always actively refetch (few queries, small payloads)
    queryClient.invalidateQueries({ queryKey: ['stageIssuesByIssueType'] }),
    queryClient.invalidateQueries({ queryKey: ['currStageIssueTypes'] }),
    queryClient.invalidateQueries({ queryKey: ['currStageUsersQuery'] }),
    queryClient.invalidateQueries({ queryKey: ['getIssuesSummary'] }),
    queryClient.invalidateQueries({ queryKey: ['getNotificationsSummary'] }),

    // Per-issue caches — on native, mark stale without mass refetch.
    // On web, refetch immediately (cache is smaller, no WKWebView constraint).
    queryClient.invalidateQueries({
      queryKey: ['singleIssue'],
      refetchType: isNative ? 'none' : 'active',
    }),
    queryClient.invalidateQueries({
      queryKey: ['singleIssueComments'],
      refetchType: isNative ? 'none' : 'active',
    }),
    queryClient.invalidateQueries({
      queryKey: ['singleIssueEvents'],
      refetchType: isNative ? 'none' : 'active',
    }),
  ])

  const tasks: Promise<unknown>[] = [invalidate, minDuration]
  tasks.push(nativeQueueManager.performSync())

  await Promise.allSettled(tasks)
}
